import { entryKey, isPartnerEntry, sameOffer } from "../identity";
import { isPointsLine, isPointsLineAsV1, pointsLineCodes } from "../entry";
import { getPartner } from "../partners";
import { partnerOf, type PartnerDirectory } from "../partners/port";
import type { BillDiscount, Sale } from "../types";
import { API_SALE_SOURCE, DISCOUNT_MODE } from "../vocabulary";

/**
 * The release plans: what a sale write must give back (points, a partner's coupon) because the
 * bill no longer stands behind it (DISCOUNT_V2_PLAN.md §9.12 "release plans", R2.4d.5; decisions
 * 60 to 63; docs/architecture/SALE_EFFECTS.md §5 for the four v1 effects they replace).
 *
 * Pure: sales in, an ordered list of steps out. A plan says WHAT to release and whether the sale
 * is written back; doing it is not here. The offline terminal, a request already in flight and the
 * network are the executor's (sale effects v2's E1), and a partner's own answer comes through the
 * port (`partners/port.ts` `release`, `refund`), so no plan names a partner or a route.
 */

/** One thing to give back. The executor runs the steps in order. */
export type ReleaseStep =
  /**
   * Refund the points line. `writeBack`: the refunded sale replaces the sale (a bill change keeps
   * the sale, minus the line); `false` for a discarded sale, which is dropped, so nothing is written.
   */
  | { kind: "refundPoints"; entry: BillDiscount; writeBack: boolean }
  /** Release the partner's coupon. `takeOff`: the entry also comes off the sale (a sale that stays); `false` for a discarded one. */
  | { kind: "releaseCoupon"; entry: BillDiscount; takeOff: boolean };

/* ── has the bill changed under its promotions? ────────────────────────────────────────────── */

/** A sale line's key, as the bill-change rule reads lines (v1's `getItemKey`): its `itemId` and `itemNumber`. */
export const saleLineKey = (item: { itemId?: unknown; itemNumber?: unknown }): string =>
  item.itemNumber === undefined || item.itemNumber === null
    ? `${item.itemId}`
    : `${item.itemId}${item.itemNumber}`;

/** Do the lines differ by membership (an item added or removed) or by a quantity? */
function linesChanged(previous: Sale["saleItems"] = [], next: Sale["saleItems"] = []): boolean {
  if (previous.length !== next.length) return true;
  const quantities = new Map<string, number>();
  previous.forEach((item) => quantities.set(saleLineKey(item), item.quantity));
  return next.some((item) => quantities.get(saleLineKey(item)) !== item.quantity || !quantities.has(saleLineKey(item)));
}

/**
 * The entries that are not the points line, as one order-independent string. 🔴 This one reads
 * `isPointsLineAsV1`, not the backend's rule: it is v1 parity (D86), so it stays equal to v1's.
 */
function otherDiscountsSignature(sale: Sale | null | undefined): string {
  return (sale?.billDiscountList ?? [])
    .filter((entry) => !isPointsLineAsV1(entry))
    .map((entry) => {
      const discount = entry as Record<string, unknown>;
      return `${discount?.discountId ?? ""}:${discount?.mid ?? ""}:${discount?.discountRate ?? ""}`;
    })
    .sort()
    .join("|");
}

/**
 * Did the bill change in a way that invalidates the points and coupons on it: a line added,
 * removed or re-quantified (a line is its `itemId` and `itemNumber`), the subtotal, or any other
 * discount? No customer signal, by design: the customer is locked while a promotion binds them
 * (`billHoldsCustomerBoundPromotion`). Rewrite of v1's `hasBillChangedForLoyalty`
 * (V1_DUPLICATES.md D86); the same answers.
 */
export const billChangedForPromotions = (previous: Sale | null | undefined, next: Sale | null | undefined): boolean =>
  linesChanged(previous?.saleItems, next?.saleItems) ||
  previous?.billSubTotal !== next?.billSubTotal ||
  otherDiscountsSignature(previous) !== otherDiscountsSignature(next);

/* ── the plans ─────────────────────────────────────────────────────────────────────────────── */

export interface BillChangeContext {
  directory: PartnerDirectory;
  /**
   * The write says "the promotions on this bill stay valid" (a reward's "Change item" adds the
   * line the reward is tied to). Replaces v1's one-shot `skipNextBillChangeInvalidation`, which
   * was module state armed before a dispatch; this is the write's own intent.
   */
  keepsPromotions?: boolean;
}

export interface DiscardContext {
  /** The merchant's partners: their `meta.discountCode`s say which entry is the points line (decision 55). */
  directory: PartnerDirectory;
}

/**
 * What a sale write that kept the same sale must give back.
 *
 * Nothing when the write keeps the promotions, when it is a different sale (a switch or a new
 * sale: `mid` differs; a discard is `planDiscard`'s), when the sale came in through the API, or
 * when the bill did not change. Otherwise, in this order:
 *
 * 1. the points line (as the backend writes it, decision 55: its `discountCode` is one of the
 *    directory's loyalty partners' codes), when the bill had one before AND still has one (the write that first
 *    applies it, and the write after the refund took it off, have no pair), refunded and written
 *    back;
 * 2. each partner coupon still on the bill that was on it before, for a partner that
 *    `releasesOnBillChange` (DotPe alone), released and taken off.
 *
 * 🔴 **A cart emptied by removing its last line keeps its `mid`, so it is a bill change** (decision
 * 61); v1 web ran the discard on it as well and could refund the line twice.
 */
export function planBillChange(
  previous: Sale | null | undefined,
  next: Sale | null | undefined,
  { directory, keepsPromotions = false }: BillChangeContext,
): ReleaseStep[] {
  if (keepsPromotions) return [];
  if (previous?.mid !== next?.mid) return [];
  if (next?.sourceInfo?.source === API_SALE_SOURCE) return [];
  if (!billChangedForPromotions(previous, next)) return [];

  const steps: ReleaseStep[] = [];
  const codes = pointsLineCodes(directory.providers);
  const isPoints = (entry: BillDiscount) => isPointsLine(entry, codes);
  const points = next?.billDiscountList?.find(isPoints);
  if (points && previous?.billDiscountList?.some(isPoints)) {
    steps.push({ kind: "refundPoints", entry: points, writeBack: true });
  }

  const before = (previous?.billDiscountList ?? []).map(entryKey);
  (next?.billDiscountList ?? [])
    .filter(isPartnerEntry)
    .filter((entry) => getPartner(partnerOf(entry, directory)).releasesOnBillChange)
    .filter((entry) => before.some((key) => sameOffer(key, entryKey(entry))))
    .forEach((entry) => steps.push({ kind: "releaseCoupon", entry, takeOff: true }));
  return steps;
}

/**
 * What a sale the operator threw away must give back (the told `discarded` ending; the sale is
 * dropped, so nothing is written back): the points line refunded, then each partner entry that is
 * neither an automatic deal nor applied by someone else, released by its own partner through the
 * port (decision 60). A catalogue deal or a manual discount has no partner to release.
 */
export function planDiscard(sale: Sale | null | undefined, { directory }: DiscardContext): ReleaseStep[] {
  const entries = sale?.billDiscountList ?? [];
  const steps: ReleaseStep[] = [];
  const codes = pointsLineCodes(directory.providers);
  const points = entries.find((entry) => isPointsLine(entry, codes));
  if (points) steps.push({ kind: "refundPoints", entry: points, writeBack: false });
  entries
    .filter((entry) => isPartnerEntry(entry) && entry.mode !== DISCOUNT_MODE.auto && !entry.appliedBy)
    .forEach((entry) => steps.push({ kind: "releaseCoupon", entry, takeOff: false }));
  return steps;
}

/**
 * A partner accepted this coupon but the priced bill does not honour it (it reduced nothing): the
 * partner has recorded a redemption the sale does not use, so release it. There is nothing to take
 * off: a write is made only when the priced bill honours it (decision 97), so the entry never reached
 * the bill. Any partner (decision 63; v1 did it only while the ACTIVE partner was Xeno). Anything
 * that is not a partner's entry has nothing to release.
 */
export const planUnhonouredCoupon = (entry: BillDiscount): ReleaseStep[] =>
  isPartnerEntry(entry) ? [{ kind: "releaseCoupon", entry, takeOff: false }] : [];
